import { STATUS_LISTING_CHIP } from '@/constants/app/marketing';
import { useBulkMarketingReviewsMutation } from '@/store/api/services/marketing/marketing';
import type { ListingView } from '@/types';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Chip from '@mui/material/Chip';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

function ineligibleReason(item: ListingView, action: 'approve' | 'reject'): string | null {
  if (action === 'approve') {
    if (item.marketing_status === 'published') return 'Already published';
    return null;
  }
  // reject
  if (item.marketing_status === 'unpublished') return 'Already unpublished';
  return null;
}

const BulkMarketingListingReview = ({ hide, props }: ModalChildrenProps) => {
  const action = props?.data?.action as 'approve' | 'reject';
  const selectedItems = (props?.data?.selectedItems ?? []) as ListingView[];
  const [bulkReviews] = useBulkMarketingReviewsMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    processed: number;
    skipped: number;
    items: ListingView[];
  } | null>(null);

  const isApprove = action === 'approve';
  const label = isApprove ? 'Approve' : 'Reject';

  const { eligible, ineligible } = useMemo(() => {
    const eligible: ListingView[] = [];
    const ineligible: { item: ListingView; reason: string }[] = [];
    for (const item of selectedItems) {
      const reason = ineligibleReason(item, action);
      if (reason) ineligible.push({ item, reason });
      else eligible.push(item);
    }
    return { eligible, ineligible };
  }, [selectedItems, action]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const grouped = new Map<number, number[]>();
      for (const item of eligible) {
        const ids = grouped.get(item.operator_id) ?? [];
        ids.push(item.location_id);
        grouped.set(item.operator_id, ids);
      }

      const results = await Promise.allSettled(
        Array.from(grouped.entries()).map(([operator_id, location_ids]) =>
          bulkReviews({
            location_ids,
            operator_id,
            action: isApprove ? 'Approve' : 'Reject',
          }).unwrap(),
        ),
      );

      const processed = results.reduce(
        (sum, r) => sum + (r.status === 'fulfilled' ? (r.value.data?.processed_count ?? 0) : 0),
        0,
      );
      const skipped = results.reduce(
        (sum, r) => sum + (r.status === 'fulfilled' ? (r.value.data?.skipped_count ?? 0) : 0),
        0,
      );
      const items = results.flatMap((r) =>
        r.status === 'fulfilled' ? (r.value.data?.items ?? []) : [],
      );

      setResult({ processed, skipped, items });
      toast.success(`Bulk ${label.toLowerCase()} completed.`);
    } catch (_err) {
      toast.error(`Failed to ${label.toLowerCase()} listings.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 2 }}>
        {!result ? (
          <>
            <Typography variant="h6" mb={1}>
              {label} {eligible.length} listing(s)?
            </Typography>
            {ineligible.length > 0 && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {ineligible.length} selected listing(s) will be skipped — hover a row to see why.
              </Typography>
            )}
            <Divider />
            <TableContainer
              sx={{ mt: 2, opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.3s' }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell>Operator</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eligible.map((item) => (
                    <TableRow key={item.location_id}>
                      <TableCell>{item.name ?? `Location ${item.location_id}`}</TableCell>
                      <TableCell>{item.operator_name ?? item.operator_id}</TableCell>
                      <TableCell>
                        {(() => {
                          const chip = STATUS_LISTING_CHIP[item.marketing_status] ?? {
                            label: item.marketing_status,
                            color: 'default' as const,
                          };
                          return (
                            <Chip
                              size="small"
                              color={chip.color}
                              label={chip.label}
                              sx={{ fontWeight: 'bold', fontSize: '11px' }}
                            />
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {ineligible.map(({ item, reason }) => (
                    <Tooltip key={item.location_id} title={reason} placement="left">
                      <TableRow sx={{ opacity: 0.4 }}>
                        <TableCell sx={{ textDecoration: 'line-through' }}>
                          {item.name ?? `Location ${item.location_id}`}
                        </TableCell>
                        <TableCell sx={{ textDecoration: 'line-through' }}>
                          {item.operator_name ?? item.operator_id}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.disabled">
                            {reason}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </Tooltip>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <>
            <Typography variant="h6" mb={1}>
              {label} complete
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, mt: 1, mb: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {result.processed}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Succeeded
                </Typography>
              </Box>
              {result.skipped > 0 && (
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {result.skipped}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Skipped
                  </Typography>
                </Box>
              )}
            </Box>
            {result.items.length > 0 && (
              <>
                <Divider />
                <TableContainer sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Location</TableCell>
                        <TableCell>New Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.items.map((item) => (
                        <TableRow key={item.location_id}>
                          <TableCell>{item.name ?? `Location ${item.location_id}`}</TableCell>
                          <TableCell>
                            {(() => {
                              const chip = STATUS_LISTING_CHIP[item.marketing_status] ?? {
                                label: item.marketing_status,
                                color: 'default' as const,
                              };
                              return (
                                <Chip
                                  size="small"
                                  color={chip.color}
                                  label={chip.label}
                                  sx={{ fontWeight: 'bold', fontSize: '11px' }}
                                />
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {result.skipped > 0 && (
              <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                Skipped listings were not eligible for this action.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!result ? (
          <>
            <Button onClick={hide}>Cancel</Button>
            <LoadingButton
              variant="contained"
              color={isApprove ? 'primary' : 'error'}
              onClick={handleSubmit}
              loading={isLoading}
              disabled={eligible.length === 0}
            >
              {label} {eligible.length > 0 ? eligible.length : ''} listing(s)
            </LoadingButton>
          </>
        ) : (
          <Button fullWidth onClick={hide}>
            Done
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default BulkMarketingListingReview;
